import { DeleteCallHistoryRecordsResponse, EndTimeSessionId, UpdateMissedCallsResponse } from '@webex/component-adapter-interfaces/dist/esm/src';
import { Observable, ReplaySubject, from } from 'rxjs';
import {
  CH_STORAGE_KEYS,
  DATE,
  DirectionTypes,
  DispositionTypes,
  ERROR_RESPONSE_TEXT,
  ICallHistoryAdapter,
  ICallHistoryRecord,
  IWebexCallHistoryResponse,
  RESPONSE_STATUS,
  SESSION_TYPES,
  VM_STORAGE_KEYS
} from '../../../../adapter-interfaces/src';
import { DISPATCH_STORAGE_EVENTS, IntegrationPhoneServicesResponse } from '../../../../adapter-interfaces/src/IEventAdapter';
import {
  removeLocalStorageItem,
  setLocalStorageItem
} from '../../../../logger/src';
import { getLocalStorageItem } from '../../../../logger/src/LocalStorage';
import { Logger } from "../../../../logger/src/Logger";
import { CallHistoryFetchOptions, deleteCallHistoryRecords, listRecentCallEvents, updateMissedCalls } from '../../services/WebexCallHistoryService';
import { processCallingSpecifics } from '../../utils/handleCallingSpecifics';
import { getLineLabelFromGuid, getManagedByName, getMultiLineLabel } from '../../utils/handleMultiLineLabel';
import { formatSIPaddress, handleNameAndAddressCheck, handlePhoneNumberCheck, handleUUID } from '../../utils/handleUUID';
import { isAISummaryFeatureEnabled } from '../../components/CallHistory/AISummary/featureGate';
import { USE_MOCK_AI_SUMMARY } from '../../components/CallHistory/AISummary/AISummaryService';
import { CALLING_BACKEND, getCallingBackEnd } from '../../utils/callingUtils';
import { getUcmLineCount } from '../../../../../components/LineSelector/utils/ucmLineCountCache';

/**
 * @description MS Teams Call History adapter handles making calls to backend
 * and formatting it for display on the client.
 */
export class WebexCallHistoryAdapter implements ICallHistoryAdapter {
  observables$: { [key: string]: ReplaySubject<ICallHistoryRecord[]> } = {};
  private lastResponses: Map<string, IWebexCallHistoryResponse> = new Map();
  private phoneServicesListener: ((event: Event) => void) | null = null;
  private hasPhoneServicesData: boolean = false;
  private aiSummaryFeatureEnabled: boolean | null = null; // Cached feature gate result
  private aiSummaryFeatureGatePromise: Promise<void> | null = null;
  // Fix: dedupe `WEBEX_INSTANCE_TOKEN_EXPIRED` storm so a burst of 401s
  // doesn't dispatch a `storage` event per response (each of which would
  // re-trigger the LandingPage re-init handler).
  private tokenExpiryFlagged: boolean = false;

  /**
   * Initialize feature gate by checking if AI summary feature is enabled.
   * In mock mode (development), enable feature without server check.
   * This should be called once during startup to cache the feature gate result.
   */
  private async initializeFeatureGate(): Promise<void> {
    if (this.aiSummaryFeatureEnabled !== null) {
      return;
    }

    if (!this.aiSummaryFeatureGatePromise) {
      this.aiSummaryFeatureGatePromise = (async () => {
        try {
          // In mock mode (development), enable feature without server check
          const isMockEnabled = USE_MOCK_AI_SUMMARY || (typeof window !== 'undefined' && (window as any).USE_MOCK_AI_SUMMARY === true);
          if (isMockEnabled) {
            this.aiSummaryFeatureEnabled = true;
            Logger.info(`[AISummaryAdapter] Mock enabled, feature gate enabled for testing`);
          } else {
            this.aiSummaryFeatureEnabled = await isAISummaryFeatureEnabled();
            Logger.info(`[AISummaryAdapter] Feature gate initialized: ${this.aiSummaryFeatureEnabled}`);
          }
        } catch (error) {
          Logger.error(`[AISummaryAdapter] Failed to initialize feature gate: ${error}`);
          this.aiSummaryFeatureEnabled = false; // Default to false on error
        } finally {
          this.aiSummaryFeatureGatePromise = null;
        }
      })();
    }

    await this.aiSummaryFeatureGatePromise;
  }

  private refreshParsedResponseAfterFeatureGate(ID: string): void {
    const lastResponse = this.lastResponses.get(ID);
    if (!lastResponse || !(ID in this.observables$)) {
      return;
    }

    this.observables$[ID].next(this.parseServerResponse(lastResponse));
  }

  private emitFallbackCallHistory(ID: string): void {
    const lastResponse = this.lastResponses.get(ID);
    this.observables$[ID]?.next(
      lastResponse ? this.parseServerResponse(lastResponse) : []
    );
  }

  private fetchCallHistory(ID: string, options: CallHistoryFetchOptions = {}): void {
    const data$ = from(listRecentCallEvents(options));
    data$.subscribe({
      next: (data) => {
        if (data.statusCode === RESPONSE_STATUS.STATUSCODE_200) {
          removeLocalStorageItem(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG);
          window.dispatchEvent(new Event("storage"));
          this.tokenExpiryFlagged = false;
          Logger.info(
            `WebexCallHistoryAdapter.listRecentCallEvents(): Call History Length: ${data?.data?.userSessions?.length}`
          );
          // Store last response for potential re-parsing when phone services data becomes available
          this.lastResponses.set(ID, data);
          this.observables$[ID].next(this.parseServerResponse(data));
          
          // Set up listener for phone services data if not already available
          this.setupPhoneServicesListener(ID);
        } else {
          if (data.statusCode === RESPONSE_STATUS.STATUSCODE_401) {
            if (!this.tokenExpiryFlagged) {
              this.tokenExpiryFlagged = true;
              setLocalStorageItem(VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED, true);
              window.dispatchEvent(new Event("storage"));
            } else {
              Logger.info(
                'WebexCallHistoryAdapter.listRecentCallEvents(): 401 already flagged, skipping duplicate token-expired dispatch.'
              );
            }
          } else if (data.statusCode >= RESPONSE_STATUS.STATUSCODE_400 && data.statusCode <= RESPONSE_STATUS.STATUSCODE_500) {
            setLocalStorageItem(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
            window.dispatchEvent(new Event("storage"));
          }
          // Keep the shared stream alive so a later refresh can recover after
          // a transient 4xx/5xx response. The storage event above continues to
          // drive the existing error-banner/re-auth handling.
          this.emitFallbackCallHistory(ID);
          Logger.error(`Could not find call history for ID "${ID}"`);
        }
      },
      error: (error: unknown) => {
        Logger.error(`WebexCallHistoryAdapter.listRecentCallEvents(): failed: ${error instanceof Error ? error.message : error}`);
        setLocalStorageItem(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
        window.dispatchEvent(new Event("storage"));
        // Do not terminate the ReplaySubject: MobileLandingPage and the
        // desktop widget must be able to receive a later successful refresh.
        this.emitFallbackCallHistory(ID);
      }
    });
  }

  /**
   * @description Parses the server response to format it for the client.
   * @param {IWebexCallHistoryResponse} response The response from the server.
   * @returns {ICallHistoryRecord[]} The formatted call history records.
   */
  parseServerResponse(
  response: IWebexCallHistoryResponse
): ICallHistoryRecord[] {
  let output: ICallHistoryRecord[] = [];

  const allowedSessionTypes = new Set<string>(Object.values(SESSION_TYPES));
  
  // Fetch phone services data from localStorage with error handling
  let phoneServicesData: IntegrationPhoneServicesResponse | null = null;
  let primaryLineGuid: string | undefined;
  let hasMultipleLines = false;

  try {
    phoneServicesData = getLocalStorageItem<IntegrationPhoneServicesResponse>(DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA);
    // Get the primary line GUID (first line = current user's primary line)
    primaryLineGuid = phoneServicesData?.data?.payload?.lines?.[0]?.GUID;
    this.hasPhoneServicesData = !!phoneServicesData;
    // Suppress per-call line indicators (L1, L2, ...) when the user has 0 or 1 configured lines.
    // Matches Webex app behavior: single-line users should not see line badges in call history.
    const configuredLinesCount = phoneServicesData?.data?.payload?.lines?.length ?? 0;
    hasMultipleLines = configuredLinesCount > 1;
    Logger.info(`[Multiline] Phone services retrieved. Lines: ${configuredLinesCount}, hasMultipleLines: ${hasMultipleLines}`);

    // UCM users do not receive Phone Services data; derive hasMultipleLines from the
    // UCM line cache populated by UcmLineService instead. Without this, the existing
    // ucmLineNumber branch below never fires for UCM users and line badges never
    // render. See UCM_LINE_SELECTION_DESIGN.md §10.
    if (!hasMultipleLines) {
      const backend = getCallingBackEnd();
      if (backend === CALLING_BACKEND.UCM) {
        const ucmLineCount = getUcmLineCount();
        hasMultipleLines = ucmLineCount > 1;
        Logger.info(`[Multiline] UCM line count from cache: ${ucmLineCount}, hasMultipleLines: ${hasMultipleLines}`);
      }
    }
  } catch (error) {
    Logger.error(`[Multiline] Error retrieving phone services data: ${error}. Call history will display without multiline features.`);
    phoneServicesData = null;
    primaryLineGuid = undefined;
    hasMultipleLines = false;
    this.hasPhoneServicesData = false;
  }

  output = response?.data?.userSessions
    ?.filter((item) => {
      return allowedSessionTypes.has(item.sessionType);
    })
    .map((value) => {
      const {
        id,
        direction,
        disposition,
        startTime,
        endTime,
        sessionType,
        durationSeconds,
        sessionId,
      } = value;

      // Multiline support: Determine line label based on session type
      // Note: Both WEBEXCALLING and WEBEXCALLING_SHARED can contain managedById
      // - WEBEXCALLING: When user (Alice) sees calls made by another user (Bob) using Alice's shared line
      //   Example: Bob uses Alice's line → Alice sees sessionType="WEBEXCALLING" with managedById=Bob's ID
      // - WEBEXCALLING_SHARED: When user (Bob) sees their own calls using someone else's line, or virtual line calls
      //   Example: Bob makes call using Alice's line → Bob sees sessionType="WEBEXCALLING_SHARED" with managedById=Bob's ID
      //
      // Performance Note: We fetch phoneServicesData once at the top of parseServerResponse and pass it
      // to all utility functions to avoid redundant localStorage reads. The utility functions have fallback
      // logic for other contexts, but here we always pass the pre-fetched data for optimal performance.
      let lineNumber = '';
      let managedByName = '';
      if (sessionType === SESSION_TYPES.WEBEXCALLING_SHARED) {
        // Virtual lines or shared line from caller's perspective
        const lineGuid = value?.self?.id;
        // Only assign a line label when the user has multiple configured lines; matches Webex app behavior.
        if (hasMultipleLines) {
          lineNumber = getLineLabelFromGuid(lineGuid, phoneServicesData); // Pass pre-fetched data
        }
        const managedByGuid = value?.callingSpecifics?.managedById;
        // Only show "Managed by" if phone services are connected AND managedById is NOT the current user (primary line)
        if (phoneServicesData && managedByGuid && managedByGuid !== primaryLineGuid) {
          managedByName = getManagedByName(managedByGuid, value, phoneServicesData); // Pass pre-fetched data
        }
      } else if (sessionType === SESSION_TYPES.WEBEXCALLING) {
        // Shared line from owner's perspective (owner sees calls made by others using their line)
        const lineGuid = value?.self?.id;
        if (hasMultipleLines) {
          lineNumber = getLineLabelFromGuid(lineGuid, phoneServicesData); // Pass pre-fetched data
        }
        const managedByGuid = value?.callingSpecifics?.managedById;
        if (phoneServicesData && managedByGuid && managedByGuid !== primaryLineGuid) {
          managedByName = getManagedByName(managedByGuid, value, phoneServicesData); // Pass pre-fetched data
        }
      } else if (value?.self?.ucmLineNumber && hasMultipleLines) {
        // UCM calling users: Use ucmLineNumber directly, but only when multiple lines are configured
        lineNumber = getMultiLineLabel(value?.self?.ucmLineNumber);
      }

      // Use cached feature gate result (feature gate checked once during device registration)
      // Default to false if feature gate not yet checked (will be true after first check)
      const isAISummaryEnabled = this.aiSummaryFeatureEnabled ?? false;

      const record: ICallHistoryRecord = {
        id,
        direction: direction as DirectionTypes,
        disposition: disposition as DispositionTypes,
        startTime,
        endTime,
        sessionType,
        callbackAddress:
          value?.self?.callbackInfo?.callbackAddress === RESPONSE_STATUS.UNKNOWN
            ? handlePhoneNumberCheck(value?.other?.callbackAddress)
            : value?.self?.callbackInfo?.callbackAddress
            ? handlePhoneNumberCheck(value?.self?.callbackInfo?.callbackAddress)
            : handlePhoneNumberCheck(value?.other?.callbackAddress),
        name: value?.other?.name
          ? formatSIPaddress(value?.other?.name)
          : handleNameAndAddressCheck(
              value?.other?.name,
              value?.self?.callbackInfo?.callbackAddress,
              value?.other?.callbackAddress
            ),
        durationSeconds,
        phoneNumber:
          value?.self?.callbackInfo?.callbackAddress === RESPONSE_STATUS.UNKNOWN
            ? handleUUID(value?.other?.callbackAddress, sessionType)
            : value?.self?.callbackInfo?.callbackAddress
            ? handleUUID(value?.self?.callbackInfo?.callbackAddress, sessionType)
            : handleUUID(value?.other?.callbackAddress, sessionType),
        callingSpecific: processCallingSpecifics(value.callingSpecifics),
        isRead: !!value?.isRead,
        lineNumber,
        managedByName,
        sessionId,
        // Conditionally populate callingContainerIds based on feature flag
        callingContainerIds: isAISummaryEnabled ? value.extensionPayload?.callingContainerIds : undefined,
        remotePartyId: value?.other?.id, // Preserve remote party user ID for presence subscriptions
      };

      return record;
    }) ?? [];

  return output;
}

  /**
   * I handle updating the subscribed observable.
   * @param {string} ID The ID for the getAll method which updates the observable with new data.
   * @param {CallHistoryFetchOptions} options Optional fetch parameters including limit, dateRange, and since.
   */
  refresh(ID: string, options: CallHistoryFetchOptions = {}): void {
    this.fetchCallHistory(ID, options);
  }

  /**
   * @description Sets up a listener to re-parse call history when phone services data becomes available.
   * This handles the race condition where call history loads before phone services data.
   * @param {string} ID The ID for the observable to update when phone services data arrives.
   */
  private setupPhoneServicesListener(ID: string): void {
    // Only set up listener if:
    // 1. Phone services data is not yet available
    // 2. Listener doesn't already exist
    if (!this.hasPhoneServicesData && !this.phoneServicesListener) {
      Logger.info('[Multiline] Phone services data not available yet. Setting up listener to re-parse when data arrives.');
      
      this.phoneServicesListener = () => {
        try {
          const phoneServicesData = getLocalStorageItem<IntegrationPhoneServicesResponse>(DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA);
          
          // Check if phone services data is now available
          const lastResponse = this.lastResponses.get(ID);
          if (phoneServicesData && !this.hasPhoneServicesData && lastResponse) {
            Logger.info('[Multiline] Phone services data detected. Re-parsing call history with multiline features.');
            this.hasPhoneServicesData = true;
            
            // Re-parse the last response with the newly available phone services data
            this.observables$[ID].next(this.parseServerResponse(lastResponse));
            
            // Clean up listener after successful re-parse
            this.cleanupPhoneServicesListener();
          }
        } catch (error) {
          Logger.error(`[Multiline] Error in phone services listener: ${error}`);
        }
      };
      
      window.addEventListener('storage', this.phoneServicesListener);
    }
  }

  /**
   * @description Cleans up the phone services storage listener to prevent memory leaks.
   */
  private cleanupPhoneServicesListener(): void {
    if (this.phoneServicesListener) {
      window.removeEventListener('storage', this.phoneServicesListener);
      this.phoneServicesListener = null;
      Logger.info('[Multiline] Phone services listener cleaned up.');
    }
  }

  getAll(ID: string, options: CallHistoryFetchOptions = {}): Observable<ICallHistoryRecord[]> {
    if (!(ID in this.observables$)) {
      this.observables$[ID] = new ReplaySubject<ICallHistoryRecord[]>();
      // Re-parse the last fetched response once the async gate resolves so AI summary IDs are not lost.
      this.initializeFeatureGate()
        .then(() => {
          this.refreshParsedResponseAfterFeatureGate(ID);
          this.refresh(ID, options);
        })
        .catch((error) => {
          Logger.error(`[AISummaryAdapter] Feature gate initialization error: ${error}`);
        });
    }
    return this.observables$[ID];
  }

  /**
   * Fetches call history without limiting the number of records. Intended for background refreshes when deduplication indicates significant data loss.
   */
  fetchCallHistoryWithoutLimit(ID: string): void {
    // force a 30-day uncapped fetch
    this.fetchCallHistory(ID, { limit: null, dateRange: DATE.MONTH });
  }

  updateMissedCalls(endTimeSessionIds: EndTimeSessionId[]): Observable<UpdateMissedCallsResponse> {
    return from(updateMissedCalls(endTimeSessionIds));
  }

  deleteCallHistoryRecords(deleteSessionIds: EndTimeSessionId[]): Observable<DeleteCallHistoryRecordsResponse> {
    return from(deleteCallHistoryRecords(deleteSessionIds));
  }

  /**
   * @description Cleanup method to remove event listeners and prevent memory leaks.
   * Should be called when the adapter is no longer needed.
   */
  disconnect(): void {
    this.cleanupPhoneServicesListener();
    this.lastResponses.clear();
    Logger.info('[Multiline] WebexCallHistoryAdapter disconnected and cleaned up.');
  }

}
